// ═══════════════════════════════════════════════════════════════
// Admin — Roles & Permissions
// Passthrough UI to GAB native role/permission system.
// Roles are created/edited/deleted directly via GAB API.
// Permission matrix controls table-level CRUD per role.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Textarea,
  Modal, ActionIcon, Stack, Table, ScrollArea, Loader, Checkbox,
  Divider,
} from '../../components/gab/GabComponents';
import { IconPlus, IconPencil, IconTrash, IconShield, IconChevronRight, IconDatabase } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import {
  listRoles, createRole, updateRole, deleteRole,
  getRoleTablePermissions, setTablePermission, listAppTables,
} from '../../lib/gabAdmin';
import type { GabRole, TablePermission, GabTable } from '../../lib/gabAdmin';
import { applyPermissionMatrix } from '../../lib/permissionMatrix';
import type { ApplyResult } from '../../lib/permissionMatrix';
import { MODULES } from '../../lib/modules';
import { TABLES } from '../../api/tables';

// Build a tableKey → module name map for the permission editor
const TABLE_MODULE_MAP: Record<string, string> = {};
for (const mod of MODULES) {
  for (const tbl of mod.tables) {
    const key = tbl as unknown as string;
    if (!TABLE_MODULE_MAP[key]) TABLE_MODULE_MAP[key] = mod.name;
  }
}

// Reverse map: tableKey → display label
const TABLE_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(TABLES).map(([label, key]) => [key as string, label])
);

interface PermRow {
  tableId: string;
  key: string;
  label: string;
  module: string;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export default function RolesTab() {
  const [roles,        setRoles]        = useState<GabRole[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editing,      setEditing]      = useState<GabRole | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GabRole | null>(null);
  const [saving,       setSaving]       = useState(false);
  const [name,         setName]         = useState('');
  const [description,  setDescription]  = useState('');

  // Apply permission matrix
  const [applyOpen,    setApplyOpen]    = useState(false);
  const [applying,     setApplying]     = useState(false);
  const [applyDone,    setApplyDone]    = useState(0);
  const [applyTotal,   setApplyTotal]   = useState(0);
  const [applyRole,    setApplyRole]    = useState('');
  const [applyResult,  setApplyResult]  = useState<ApplyResult | null>(null);

  // Permission editor
  const [permRole,     setPermRole]     = useState<GabRole | null>(null);
  const [permRows,     setPermRows]     = useState<PermRow[]>([]);
  const [allTables,    setAllTables]    = useState<GabTable[]>([]);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [savingPerm,   setSavingPerm]   = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const data = await listRoles().catch(() => []);
    setRoles(data.filter(r => !r.isSystem));
    setLoading(false);
  };

  const openCreate = () => { setEditing(null); setName(''); setDescription(''); setModalOpen(true); };
  const openEdit   = (r: GabRole) => { setEditing(r); setName(r.name); setDescription(r.description ?? ''); setModalOpen(true); };

  const handleSave = async () => {
    if (!name.trim()) { notifications.show({ message: 'Role name required', color: 'var(--color-error)' }); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateRole(editing.id, name.trim(), description.trim());
        notifications.show({ message: 'Role updated', color: 'var(--color-success)' });
      } else {
        await createRole(name.trim(), description.trim());
        notifications.show({ message: 'Role created', color: 'var(--color-success)' });
      }
      setModalOpen(false);
      load();
    } catch {
      notifications.show({ message: 'Failed to save role', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRole(deleteTarget.id);
      notifications.show({ message: `"${deleteTarget.name}" deleted`, color: '#939598' });
      setDeleteTarget(null);
      load();
    } catch {
      notifications.show({ message: 'Failed to delete role', color: 'var(--color-error)' });
    }
  };

  const handleApplyMatrix = async () => {
    setApplying(true);
    setApplyDone(0);
    setApplyTotal(0);
    setApplyResult(null);
    try {
      const result = await applyPermissionMatrix((done, total, role) => {
        setApplyDone(done);
        setApplyTotal(total);
        setApplyRole(role);
      });
      setApplyResult(result);
      if (result.failed === 0) {
        notifications.show({ message: `Permission matrix applied — ${result.succeeded} permissions set`, color: 'var(--color-success)' });
      } else {
        notifications.show({ message: `Applied with ${result.failed} errors`, color: '#C68700' });
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      notifications.show({ message: `Failed: ${msg}`, color: 'var(--color-error)' });
    } finally {
      setApplying(false);
    }
  };

  const openPermissions = async (role: GabRole) => {
    setPermRole(role);
    setLoadingPerms(true);
    try {
      const [perms, tables] = await Promise.all([
        getRoleTablePermissions(role.id),
        allTables.length ? Promise.resolve(allTables) : listAppTables(),
      ]);
      if (!allTables.length) setAllTables(tables);

      // Role-specific endpoint returns tableId (UUID), not key (base36).
      // Build the map by UUID so we can look up by t.id from listAppTables.
      const permMap: Record<string, TablePermission> = {};
      for (const p of perms) {
        const uuid = p.tableId ?? p.id;
        if (uuid) permMap[uuid] = p;
      }

      const rows: PermRow[] = tables
        .filter(t => TABLE_MODULE_MAP[t.key] || TABLE_LABELS[t.key])
        .map(t => {
          const p = permMap[t.id];
          return {
            tableId:   t.id,
            key:       t.key,
            label:     TABLE_LABELS[t.key] ?? t.name,
            module:    TABLE_MODULE_MAP[t.key] ?? '—',
            canView:   p?.canView   ?? false,
            canAdd:    p?.canAdd    ?? false,
            canEdit:   p?.canEdit   ?? false,
            canDelete: p?.canDelete ?? false,
          };
        })
        .sort((a, b) => a.module.localeCompare(b.module) || a.label.localeCompare(b.label));

      setPermRows(rows);
    } finally { setLoadingPerms(false); }
  };

  const togglePerm = async (row: PermRow, field: 'canView' | 'canAdd' | 'canEdit' | 'canDelete') => {
    if (!permRole) return;
    const newVal = !row[field];
    setSavingPerm(row.key + field);
    try {
      const patch: Record<string, unknown> = {};
      if (field === 'canView')   patch.viewAccess   = newVal ? 'all' : 'none';
      if (field === 'canAdd')    patch.canAdd        = newVal;
      if (field === 'canEdit')   patch.editAccess   = newVal ? 'all' : 'none';
      if (field === 'canDelete') patch.deleteAccess = newVal ? 'all' : 'none';
      await setTablePermission(permRole.id, row.tableId, patch as any);
      setPermRows(prev => prev.map(r => r.key === row.key ? { ...r, [field]: newVal } : r));
    } catch {
      notifications.show({ message: 'Failed to update permission', color: 'var(--color-error)' });
    } finally { setSavingPerm(null); }
  };

  const setAllForModule = async (module: string, grant: boolean) => {
    if (!permRole) return;
    const rows = permRows.filter(r => r.module === module);
    for (const row of rows) {
      try {
        await setTablePermission(permRole.id, row.tableId, {
          viewAccess:   grant ? 'all' : 'none',
          canAdd:       grant,
          editAccess:   grant ? 'all' : 'none',
          deleteAccess: grant ? 'all' : 'none',
        });
      } catch {}
    }
    setPermRows(prev => prev.map(r =>
      r.module === module ? { ...r, canView: grant, canAdd: grant, canEdit: grant, canDelete: grant } : r
    ));
    notifications.show({ message: `${grant ? 'Granted' : 'Revoked'} all permissions for ${module}`, color: grant ? 'var(--color-success)' : '#939598' });
  };

  // Group permission rows by module
  const moduleGroups = useMemo(() => {
    const groups: Record<string, PermRow[]> = {};
    for (const row of permRows) {
      if (!groups[row.module]) groups[row.module] = [];
      groups[row.module].push(row);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [permRows]);

  // ── Permission Editor ─────────────────────────────────────────
  if (permRole) {
    return (
      <div>
        <Group mb="md">
          <Button variant="subtle" size="xs" onClick={() => setPermRole(null)}>← Back to Roles</Button>
          <Text fw={600}>{permRole.name} — Permissions</Text>
          <Badge size="sm" color="var(--color-primary-700)">Table-level CRUD</Badge>
        </Group>

        {loadingPerms ? (
          <Group justify="center" py="xl"><Loader size="sm" /></Group>
        ) : (
          <Stack gap="md">
            {moduleGroups.map(([module, rows]) => {
              const allGranted = rows.every(r => r.canView && r.canAdd && r.canEdit && r.canDelete);
              return (
                <Paper key={module} p="md" withBorder>
                  <Group justify="space-between" mb="sm">
                    <Text fw={600} size="sm">{module}</Text>
                    <Group gap={6}>
                      <Button size="xs" variant="subtle" color="var(--color-success)"
                        onClick={() => setAllForModule(module, true)} disabled={allGranted}>
                        Grant All
                      </Button>
                      <Button size="xs" variant="subtle" color="var(--color-error)"
                        onClick={() => setAllForModule(module, false)} disabled={!allGranted}>
                        Revoke All
                      </Button>
                    </Group>
                  </Group>
                  <Table withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Table</Table.Th>
                        <Table.Th w={70} ta="center">View</Table.Th>
                        <Table.Th w={70} ta="center">Add</Table.Th>
                        <Table.Th w={70} ta="center">Edit</Table.Th>
                        <Table.Th w={70} ta="center">Delete</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {rows.map(row => (
                        <Table.Tr key={row.key}>
                          <Table.Td><Text size="xs" c="dimmed" ff="monospace">{row.label}</Text></Table.Td>
                          {(['canView', 'canAdd', 'canEdit', 'canDelete'] as const).map(f => (
                            <Table.Td key={f} ta="center">
                              <Checkbox
                                size="xs"
                                checked={row[f]}
                                onChange={() => togglePerm(row, f)}
                                disabled={savingPerm === row.key + f}
                              />
                            </Table.Td>
                          ))}
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Paper>
              );
            })}
          </Stack>
        )}
      </div>
    );
  }

  // ── Role List ─────────────────────────────────────────────────
  return (
    <>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Roles</Title>
        <Group gap={6}>
          <Button leftSection={<IconDatabase size={14} />} size="xs" variant="light" color="var(--data-viz-650)"
            onClick={() => { setApplyOpen(true); setApplyResult(null); }}>
            Apply Permission Matrix
          </Button>
          <Button leftSection={<IconPlus size={14} />} size="xs" onClick={openCreate}>
            Add Role
          </Button>
        </Group>
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        Roles are defined in GAB and apply across all OpenGov products. Changes here affect all apps sharing this tenant.
      </Text>

      <Paper withBorder>
        <ScrollArea>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Role</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th style={{ width: 140 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading && (
                <Table.Tr><Table.Td colSpan={3}><Group justify="center" py="md"><Loader size="sm" /></Group></Table.Td></Table.Tr>
              )}
              {!loading && roles.length === 0 && (
                <Table.Tr><Table.Td colSpan={3}><Text c="dimmed" ta="center" py="lg">No roles defined</Text></Table.Td></Table.Tr>
              )}
              {roles.map(role => (
                <Table.Tr key={role.id}>
                  <Table.Td>
                    <Group gap={6}>
                      <IconShield size={14} color="#7B7D7F" />
                      <Text size="sm" fw={500}>{role.name}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td><Text size="sm" c="dimmed" lineClamp={1}>{role.description}</Text></Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Button size="xs" variant="subtle" rightSection={<IconChevronRight size={12} />}
                        onClick={() => openPermissions(role)}>
                        Permissions
                      </Button>
                      <ActionIcon variant="subtle" size="sm" onClick={() => openEdit(role)}>
                        <IconPencil size={13} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" color="var(--color-error)" size="sm" onClick={() => setDeleteTarget(role)}>
                        <IconTrash size={13} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Role' : 'Create Role'} size="sm">
        <Stack gap="sm">
          <TextInput label="Role Name" required value={name} onChange={e => setName(e.target.value)} />
          <Textarea label="Description" value={description} onChange={e => setDescription(e.target.value)} rows={2} />
          <Divider />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>{editing ? 'Save' : 'Create'}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirm */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Role" size="sm">
        <Text mb="md">Delete <strong>{deleteTarget?.name}</strong>? This removes the role from all assigned users.</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="var(--color-error)" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>

      {/* Apply Permission Matrix */}
      <Modal opened={applyOpen} onClose={() => { if (!applying) setApplyOpen(false); }}
        title="Apply Permission Matrix" size="sm">
        {!applying && !applyResult && (
          <Stack gap="sm">
            <Text size="sm">
              This will set table-level CRUD permissions for all 35 roles across all 19 modules
              (~700 API calls). Existing permissions will be overwritten.
            </Text>
            <Text size="sm" c="dimmed">
              Custom-access roles (volunteers, CERT members, T&L portal, shelter volunteers)
              will be scoped to their own records only.
            </Text>
            <Divider />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setApplyOpen(false)}>Cancel</Button>
              <Button color="var(--data-viz-650)" leftSection={<IconDatabase size={14} />} onClick={handleApplyMatrix}>
                Apply Now
              </Button>
            </Group>
          </Stack>
        )}
        {applying && (
          <Stack gap="md" align="center" py="md">
            <Loader size="md" />
            <Text size="sm" fw={500}>Applying permissions…</Text>
            <Text size="xs" c="dimmed">{applyRole.replace(/_/g, ' ')}</Text>
            <Text size="sm">{applyDone} / {applyTotal || '…'}</Text>
            {applyTotal > 0 && (
              <div style={{ width: '100%', background: '#e5e7eb', borderRadius: 4, height: 8 }}>
                <div style={{
                  width: `${Math.round((applyDone / applyTotal) * 100)}%`,
                  background: '#7c3aed', height: 8, borderRadius: 4, transition: 'width 0.2s',
                }} />
              </div>
            )}
          </Stack>
        )}
        {!applying && applyResult && (
          <Stack gap="sm">
            <Group gap={8}>
              <Badge color="var(--color-success)">{applyResult.succeeded} set</Badge>
              {applyResult.failed > 0 && <Badge color="var(--color-error)">{applyResult.failed} failed</Badge>}
              {applyResult.skipped > 0 && <Badge color="#939598">{applyResult.skipped} skipped</Badge>}
            </Group>
            {applyResult.errors.length > 0 && (
              <ScrollArea h={120}>
                <Stack gap={2}>
                  {applyResult.errors.map((e, i) => (
                    <Text key={i} size="xs" c="var(--color-error)" ff="monospace">{e}</Text>
                  ))}
                </Stack>
              </ScrollArea>
            )}
            <Divider />
            <Group justify="flex-end">
              <Button onClick={() => setApplyOpen(false)}>Done</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
